<template>

    <AppHead :title="user.name" />


    <section class="px-4 xl:px-0 pb-12">
        <div class="flex flex-col justify-center md:flex-row md:space-x-6 md:bg-white rounded-2xl p-0 pt-4 md:p-6">
            <div class="mb-4 md:mb-0">
                <div class="w-full flex items-center justify-center">
                    <div class="shrink-0 mb-3 w-48 h-48 flex items-center justify-center rounded-full bg-center bg-cover bg-gray-400 border-2 border-white" :style="'background-image: url(' + user.avatar + ')'" >
                        <svg v-if="!user.avatar" class="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                    </div>
                </div>


                <div class="w-full flex flex-col items-center">
                    <div v-if="!user.avatar && !avatarUploading" @click="$refs.avatar.click()" class="inline-flex border border-gray-400 rounded-full h-12 px-3 pr-4 space-x-1.5 items-center text-gray-800 hover:border-gray-700 hover:text-gray-900 cursor-pointer transition-colors duration-100 ease-out active:border-gray-900">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                        <div class="font-medium">
                            Upload photo
                        </div>
                    </div>
                    <div v-if="avatarUploading" class="inline-flex border border-gray-400 rounded-full h-12 px-3 pr-4 space-x-1.5 items-center text-gray-700">
                        <svg class="animate-spin h-5 w-5 mr-1 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <div class="font-medium">
                            Working
                        </div>
                    </div>
                    <Link v-if="user.avatar && !avatarUploading" :href="route('users.update', user)" :data="{ avatar: 'delete'}" as="button" method="patch" class="inline-flex border bg-white border-gray-400 rounded-full h-12 px-3 pr-4 space-x-1.5 items-center text-gray-800 hover:border-gray-700 hover:text-gray-900 cursor-pointer transition-colors duration-100 ease-out active:border-gray-900">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                        <div class="font-medium">
                            Remove photo
                        </div>
                    </Link>
                </div>
            </div>

            <div class="flex-1 flex flex-col justify-between">
                <div class="flex justify-between w-full items-center mb-3 md:mb-0">
                    <div class="text-3xl font-bold tracking-tight line-clamp-1">
                        {{ user.name }}
                    </div>
                    <div @click="open = true" class="shrink-0 bg-white flex border border-gray-400 rounded-full h-12 w-12 justify-center items-center text-gray-800 hover:border-gray-700 hover:text-gray-900 cursor-pointer transition-colors duration-100 ease-out active:border-gray-900">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path></svg>
                    </div>
                </div>
                <div class="w-full mb-6 md:mb-0 flex items-center">
                    <div class="mr-1.5 -ml-1 md:ml-0 px-2 py-1 inline-flex text-xs font-semibold rounded-full bg-gray-400 shrink-0">
                        {{ user.role }}
                    </div>
                    <div class="text-sm font-medium text-gray-700 line-clamp-1">
                        {{ user.title }} · {{ user.timezone }}
                    </div>
                </div>
                <div class="flex w-full shrink-0 space-x-2 h-12 flex-1 justify-between md:justify-end md:items-end">
                    <a :href="'mailto:' + user.email" class="flex bg-white border border-gray-400 rounded-full h-12 px-3 pr-4 space-x-1.5 items-center text-gray-800 hover:border-gray-700 hover:text-gray-900 cursor-pointer transition-colors duration-100 ease-out active:border-gray-900">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path></svg>
                        <div class="font-medium">
                            Send message
                        </div>
                    </a>
                    <Link :href="route('logout')" class="flex bg-white border border-gray-400 rounded-full h-12 px-3 pr-4 space-x-1.5 items-center text-gray-800 hover:border-gray-700 hover:text-gray-900 cursor-pointer transition-colors duration-100 ease-out active:border-gray-900">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path></svg>
                        <div class="font-medium">
                            Sign out
                        </div>
                    </Link>
                </div>
            </div>
        </div>
    </section>

    <section class="px-4 xl:px-0 pb-16">
        <SectionHeading title="Notifications" importance="2" />
        <div class="bg-white w-full rounded-2xl">
            <div class="grid grid-cols-4 p-8 gap-6 gap-y-10 items-center">
                <div class="col-span-3 font-medium">Marketing Emails</div>
                <div class="col-span-1 flex justify-end">
                    <Switch v-model="marketingEmailsSubscriber" :class="[marketingEmailsSubscriber ? 'bg-regal-blue-700' : 'bg-gray-400', 'relative inline-flex shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-regal-blue-500']">
                        <span class="sr-only">Receive marketing emails</span>
                        <span aria-hidden="true" :class="[marketingEmailsSubscriber ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition ease-in-out duration-200']" />
                    </Switch>
                </div>

                <div class="col-span-3 font-medium">Product Updates</div>
                <div class="col-span-1 flex justify-end">
                    <Switch v-model="productUpdatesSubscriber" :class="[productUpdatesSubscriber ? 'bg-regal-blue-700' : 'bg-gray-400', 'relative inline-flex shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-regal-blue-500']">
                        <span class="sr-only">Receive product updates</span>
                        <span aria-hidden="true" :class="[productUpdatesSubscriber ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition ease-in-out duration-200']" />
                    </Switch>
                </div>
            </div>
        </div>
    </section>

    <form @submit.prevent="form.post('/users/' + user)">

    </form>

    <Modal v-model:open="open">
        <div class="w-full">
            <form @submit.prevent="updateUser">
                <div class="space-y-8">
                    <div class="grid grid-cols-4 gap-x-6">
                        <div class="">
                            <label for="name" class="block font-medium text-gray-800 sm:mt-px sm:pt-2">
                                Name
                            </label>
                        </div>
                        <div class="col-span-3">
                            <input type="text" name="name" v-model="form.name" class="rounded-lg w-full" required>
                            <InlineError v-if="form.errors.name" :message="form.errors.name"/>
                        </div>
                    </div>
                    <div class="grid grid-cols-4 gap-x-6">
                        <div class="">
                            <label for="title" class="block font-medium text-gray-800 sm:mt-px sm:pt-2">
                                What I do
                            </label>
                        </div>
                        <div class="col-span-3">
                            <input type="text" name="title" v-model="form.title" class="rounded-lg w-full" required>
                            <InlineError v-if="form.errors.title" :message="form.errors.title"/>
                        </div>
                    </div>
                    <div class="w-full flex justify-center border-t border-t-gray-400 pt-8 justify-between">
                        <div @click="deleteUser">
                            <Button label="Delete profile" :disabled="form.processing" type="danger"/>
                        </div>
                        <button type="submit">
                            <Button label="Save" :disabled="form.processing"/>
                        </button>
                    </div>
                </div>
            </form>
        </div>
    </Modal>

    <input type="file" accept="image/*" ref="avatar" @input="uploadAvatar" class="hidden"/>
</template>

<script>
    import { useForm } from '@inertiajs/inertia-vue3'
    import { computed } from 'vue'
    import Layout from '@/Shared/Layout'
    import SectionHeading from '@/Shared/SectionHeading'
    import Modal from '@/Shared/Modal'
    import Button from '@/Shared/Button'
    import { ref } from 'vue'
    import InlineError from '@/Shared/InlineError'
    import { Switch } from '@headlessui/vue'
    import { Inertia } from '@inertiajs/inertia'

    export default {
        setup (props) {
            const form = useForm({
                _method: 'patch',
                name: props.user.name ?? null,
                title: props.user.title ?? null,
                avatar: null,
            })
            const open = ref(false)
            const avatarUploading = ref(false)
            const marketingEmailsSubscriber = ref(props.user.marketingEmailsSubscriber)
            const productUpdatesSubscriber = ref(props.user.productUpdatesSubscriber)

            return { form, open, avatarUploading, marketingEmailsSubscriber, productUpdatesSubscriber }
        },
        components: { SectionHeading, Modal, Button, InlineError, Switch },
        props: {
            user: Object,
            avatar: String,
        },
        watch: {
            marketingEmailsSubscriber(newVal, oldVal) {
                Inertia.patch(route('users.update', this.user), { marketingEmailsSubscriber: newVal }, {

                })
            },
            productUpdatesSubscriber(newVal, oldVal) {
                Inertia.patch(route('users.update', this.user), { productUpdatesSubscriber: newVal }, {

                })
            }
        },
        methods: {
            updateUser() {
                this.form.patch(route('users.update', this.user.uuid), {
                    onSuccess: () => {
                        this.open = false
                        this.avatarUploading = false
                        this.form.reset('avatar')
                    }
                })
            },
            deleteUser() {
                if (window.confirm('Do you want to delete ' + this.user.name + ' permanently? This can‘t be undone.')) {
                    this.$inertia.delete(route('users.destroy', this.user))
                }
            },
            uploadAvatar() {
                this.avatarUploading = true
                Vapor.store(this.$refs.avatar.files[0], {
                    progress: progress => {
                        this.uploadProgress = Math.round(progress * 100)
                    }
                }).then(response => {
                    this.form.avatar = {
                        uuid: response.uuid,
                        key: response.key,
                        bucket: response.bucket,
                        name: this.$refs.avatar.files[0].name,
                        content_type: this.$refs.avatar.files[0].type,
                        extension: this.$refs.avatar.files[0].name.split('.').pop() ?? null,
                    }
                })
                .then(r => {
                    this.updateUser()
                })
            },
        },
        layout: Layout,
    }
</script>
